import { useBrandManager } from './useBrandManager';
import { useAuth } from './useAuth';
import { useLocation } from 'react-router-dom';

export interface UserContext {
  page: string;
  previousPage: string;
  theme: string;
  role?: string;
  brandId?: string;
  brandName?: string;
  timestamp: string;
  browser: string;
  screenSize: string;
  viewport: string;
  language: string;
  timezone: string;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  userAgent: string;
  recentPages: string[];
  sessionDuration: number;
}

export const useUserContext = () => {
  const { currentBrand } = useBrandManager();
  const { user } = useAuth();
  const location = useLocation();

  const captureContext = (): UserContext => {
    let recentPages: string[] = [];
    let updatedPages: string[] = [];
    let sessionDuration = 0;
    
    // Safely access sessionStorage with try-catch
    try {
      const recentPagesStr = sessionStorage.getItem('recentPages');
      recentPages = recentPagesStr ? JSON.parse(recentPagesStr) : [];
      
      updatedPages = [location.pathname, ...recentPages.filter((p: string) => p !== location.pathname)].slice(0, 5);
      sessionStorage.setItem('recentPages', JSON.stringify(updatedPages));
      
      const sessionStart = sessionStorage.getItem('sessionStart');
      if (!sessionStart) {
        sessionStorage.setItem('sessionStart', new Date().toISOString());
      }
      sessionDuration = sessionStart 
        ? Math.floor((new Date().getTime() - new Date(sessionStart).getTime()) / 60000)
        : 0;
    } catch (e) {
      console.warn('SessionStorage unavailable:', e);
      // Fallback: use only current page
      updatedPages = [location.pathname];
    }
    
    // Detect device type
    const width = window.innerWidth;
    let deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop';
    if (width < 768) deviceType = 'mobile';
    else if (width < 1024) deviceType = 'tablet';
    
    return {
      page: location.pathname,
      previousPage: recentPages[1] || document.referrer || '',
      theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
      brandId: currentBrand?.id,
      brandName: currentBrand?.name,
      timestamp: new Date().toISOString(),
      browser: navigator.userAgent,
      screenSize: `${window.innerWidth}x${window.innerHeight}`,
      viewport: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      deviceType,
      userAgent: navigator.userAgent,
      recentPages: updatedPages,
      sessionDuration,
    };
  };

  return { captureContext };
};
